import { clone, getQueryParameterByName, mergeRecursive } from '../common';

/**
 * Filter any items to be excluded for products.
 *
 * @param {array} items
 * @param {Object} userData
 * @return array
 */
export const getShowForProducts = (items, userData) => {
  const products = userData ? userData.basketList : getQueryParameterByName('products');
  return items.filter(section => {
    if (!Object.hasOwnProperty.call(section, 'showForProducts')) {
      // The section has not set the showForProducts. Always show.
      return true;
    }

    // Check that there are any intersection in the arrays. If so the section is valid.
    const intersection = section.showForProducts.filter(value => {
      return products.indexOf(value) !== -1;
    });

    return intersection.length > 0;
  });
};

/**
 * Get a merged component object.
 * @param  {object} component            The section data.
 * @param  {object} components  The section components.
 * @return {object}                    The section object.
 */
const getComponent = (component, components) => {
  let newComponent = component;
  if (component.alias && components) {
    let inheritComponent;
    // Allow for section components.
    if (newComponent.alias && components[newComponent.alias]) {
      // Allow for alias/inheritance in sectionComponents. Only one level.
      if (components[newComponent.alias].alias &&
        components[components[newComponent.alias].alias]) {
        inheritComponent = mergeRecursive(
          clone(components[components[newComponent.alias].alias]),
          components[newComponent.alias]
        );
      } else {
        inheritComponent = clone(components[newComponent.alias]);
      }
      newComponent = mergeRecursive(inheritComponent, newComponent);
    }
  }

  return newComponent;
};

/**
 * Filters out any section that are not wanted based on the URL query string param "products".
 * @param  {Object} packageData The package data JSON.
 * @return {Object}             The processed package data.
 */
export const processPackageData = (packageData) => {
  const sections = packageData.sections.map(section => {
    return getComponent(section, packageData.sectionComponents);
  });
  const validSections = getShowForProducts(sections);

  // Now loop each valid section to find the valid pages.
  validSections.forEach((section, index) => {
    if (!section.pages) {
      // eslint-disable-next-line no-throw-literal
      throw `Section alias: ${section.alias} don't exists`;
    }
    const pages = section.pages.map(page => {
      return getComponent(page, packageData.pageComponents);
    });
    validSections[index].pages = getShowForProducts(pages);
  });

  // Now loop each valid page to find the valid steps.
  validSections.forEach((section, sIndex) => {
    section.pages.forEach((page, pIndex) => {
      if (!page.steps) {
        // eslint-disable-next-line no-throw-literal
        throw `Page alias: ${page.alias} don't exists`;
      }
      const steps = page.steps.map(step => {
        return getComponent(step, packageData.stepComponents);
      });
      validSections[sIndex].pages[pIndex].steps = getShowForProducts(steps);
    });
  });

  // Now loop each valid page to find the valid steps.
  validSections.forEach((section, sIndex) => {
    section.pages.forEach((page, pIndex) => {
      page.steps.forEach((step, stIndex) => {
        if (!step.component.props) {
          return;
        }
        validSections[sIndex].pages[pIndex].steps[stIndex].component.props =
          getShowForProducts(step.component.props);
      });
    });
  });

  const newPackageData = packageData;

  if (packageData.campaigns) {
    packageData.campaigns.forEach(campaign => {
      // Only activate campaign for current product.
      if (campaign.isActive &&
        (campaign.showForProducts.includes(packageData.initialValues.packageName) ||
          !campaign.showForProducts)) {
        newPackageData.modals[campaign.id] = campaign.modal;

        if (newPackageData.initialValues.campaign
          && newPackageData.initialValues.campaign.productSelector
          && newPackageData.initialValues.campaign.productSelector.advantages
          && campaign.componentData
          && campaign.componentData.productSelector
          && campaign.componentData.productSelector.advantages) {
          campaign.componentData.productSelector.advantages
            .push(...newPackageData.initialValues.campaign.productSelector.advantages);
        }

        newPackageData.initialValues.campaign = {
          ...campaign.componentData,
          icon: campaign.icon,
          showForProducts: campaign.showForProducts,
          showForPackages: campaign.showForPackages,
        };
        newPackageData.initialValues.insertToState = packageData.initialValues.insertToState || [];
        packageData.initialValues.insertToState.push('campaign');
      }
    });
  }

  newPackageData.sections = validSections;
  return newPackageData;
};

/**
 * Sorts and returns an array of sectionIds in the order they should be shown.
 * @param  {object} packageData The raw data of the package.
 * @return {array}              Array of section ids in correct order.
 */
export const getSectionOrder = (packageData) => {
  const sections = packageData.sections.map(section => {
    const id = section.sectionId;
    const weight = section.sectionWeight;
    return {
      id,
      weight,
    };
  });
  sections.sort((a, b) => {
    return a.weight - b.weight;
  });
  return sections.map(section => section.id);
};

/**
 * Fetch a section object from ID.
 * @param  {integer|Number} id          The ID of the section to find.
 * @param  {object}  packageData The raw data of the package.
 * @param  {boolean} returnIndex The raw data of the package.
 * @return {object}              The section object.
 */
export const getSectionById = (id, packageData, returnIndex = false) => {
  if (!packageData || !packageData.sections) {
    return {};
  }
  if (returnIndex) {
    return packageData.sections.findIndex(section => section.sectionId === id);
  }
  return packageData.sections.find(section => section.sectionId === id);
};

/**
 * Sorts and returns an array of pageIds (stepIDs) in the order they should be shown.
 * @param  {object} sectionData The raw data of the section.
 * @return {array}             Array of page (step) ids in correct order.
 */
export const getPagesOrder = (sectionData) => {
  const pages = sectionData.pages.map(page => {
    const id = page.pageId;
    const weight = page.pageWeight;
    return {
      id,
      weight,
    };
  });
  pages.sort((a, b) => {
    return a.weight - b.weight;
  });
  return pages.map(page => page.id);
};

/**
 * Fetch a page object from ID.
 * @param  {integer|Number} id          The ID of the section to find.
 * @param  {object} sectionData  The raw data of the section.
 * @param  {boolean} returnIndex The raw data of the package.
 * @return {object}              The section object.
 */
export const getPageById = (id, sectionData, returnIndex = false) => {
  if (!sectionData || !sectionData.pages) {
    return {};
  }
  if (returnIndex) {
    return sectionData.pages.findIndex(page => page.pageId === id);
  }
  return sectionData.pages.find(page => page.pageId === id);
};

/**
 * Sorts and returns an array of stepIds in the order they should be shown.
 * @param  {object} pageData The data of the page.
 * @return {array}             Array of page (step) ids in correct order.
 */
export const getStepsOrder = (pageData) => {
  const steps = pageData.steps.map(step => {
    const id = step.stepId;
    const weight = step.stepWeight;
    return {
      id,
      weight,
    };
  });
  steps.sort((a, b) => {
    return a.weight - b.weight;
  });
  return steps.map(step => step.id);
};

/**
 * Fetch a step object from ID.
 * @param  {integer|Number} id          The ID of the step to find.
 * @param  {object} pageData     The data of the page.
 * @param  {boolean} returnIndex The raw data of the package.
 * @return {object}              The step object.
 */
export const getStepById = (id, pageData, returnIndex = false) => {
  if (!pageData || !pageData.steps) {
    return {};
  }
  if (returnIndex) {
    return pageData.steps.findIndex(step => step.stepId === id);
  }
  return pageData.steps.find(step => step.stepId === id);
};

/**
 * Get the steps that should be rendered
 * @param  {object} pageData          The data of the page.
 * @param  {object} currentProgress   The data of the current progress.
 * @return {array}                    Steps that should be rendered.
 */
export const getStepIndex = (pageData, currentProgress) => {
  if (Object.keys(pageData).length === 0) {
    return [];
  }
  const {
    currentPageData: {
      currentPageId,
      currentStepData: {
        currentStepId,
      },
    },
  } = currentProgress;
  if (currentPageId !== pageData.pageId) {
    // Now just return the first step as this is the next page.
    const nextPageStepOrder = getStepsOrder(pageData);
    const nextStepId = nextPageStepOrder[0];
    return pageData.steps.findIndex(step => step.stepId === nextStepId);
  }
  return pageData.steps.findIndex(step => step.stepId === currentStepId);
};

/**
 * Get the steps that should be rendered
 * @param  {object} pageData          The data of the page.
 * @param  {object} currentProgress   The data of the current progress.
 * @param  {object} stepComponents    Generic step components.
 * @return {array}                    Steps that should be rendered.
 */
export const getStepsForRender = (pageData, currentProgress) => {
  if (Object.keys(pageData).length === 0) {
    return [];
  }
  const {
    currentPageData: {
      currentPageId,
      currentStepData: {
        currentStepId, finishedSteps,
      },
    },
  } = currentProgress;
  if (currentPageId !== pageData.pageId) {
    // Nothing to return if we are not at the right page.
    return [];
  }
  // First render finished steps.
  let steps = [];
  if (finishedSteps.length !== 0) {
    steps = finishedSteps.map(finishedStep => {
      return getStepById(finishedStep, pageData);
    });
    // If current step is already finished
    if (finishedSteps.includes(currentStepId)) {
      return steps;
    }
  }
  // Render the current step
  const step = getStepById(currentStepId, pageData);
  if (!step) {
    return [];
  }
  steps.push(step);
  return steps;
};

/**
 * Get the index of the id
 * @param   {string} id           The ID to get
 * @param   {object} packageOrder The object to retrieve the ID from
 * @returns {string}
 */
export const getOrderIndex = (id, packageOrder) => {
  return packageOrder.find(orderItem => orderItem === id);
};

/**
 * Return the length of the progress bar (1=100%)
 * @param   {array} sections       The list of sections
 * @param   {object} currentProgress    The current progression
 * @returns {number}                    The number being used to render the length.
 */
export const getProgressLength = (sections, currentProgress) => {
  const {
    currentSectionId,
    finishedSections,
    currentPageData: {
      finishedPages,
    },
  } = currentProgress;

  let progressLength = 0;
  const maxLength = 1;
  const sectionSize = sections.length - 1;
  const sectionLength = maxLength / sectionSize;
  let finishedLength = -sectionLength;

  sections.forEach(section => {
    if (finishedSections.includes(section.sectionId)) {
      progressLength += sectionLength;
      finishedLength += sectionLength;
    }

    const pages = section.pages.length;
    const pageLength = sectionLength / pages;
    section.pages.forEach(page => {
      if (section.sectionId === currentSectionId) {
        if (finishedPages.includes(page.pageId)) {
          if (section.sectionPageProgress !== false) {
            progressLength += pageLength;
          }
        }
      }
    });
  });

  progressLength = progressLength <= maxLength ? progressLength : maxLength;
  return {
    progressLength,
    finishedLength: finishedLength <= 0 ? 0 : finishedLength,
  };
};

/**
 * Return the length of the progress bar (1=100%)
 * @param   {array}  sections         The list of sections
 * @param   {object} currentProgress  The current progression
 * @param   {array}  sectionOrder     The list of sections
 * @returns {boolean}                 If this is the last section.
 */
export const isLastSection = (sections, currentProgress, sectionOrder) => {
  const {
    currentSectionId,
    currentPageData: {
      currentPageId,
      pagesOrder,
    } } = currentProgress;
  if (currentPageId === pagesOrder[pagesOrder.length - 1] &&
    currentSectionId === sectionOrder[sectionOrder.length - 1]) {
    return true;
  }
  return false;
};

/**
 * Return the active sections.
 * @param  {Object} packageData The full package data.
 * @param  {string} currentSectionGroup The current progress object.
 * @return {array}  active sections.
 */
export const getSections = (packageData, currentSectionGroup) => {
  return packageData.sections.filter(section => section.sectionGroup === currentSectionGroup);
};

/**
 * /**
 * Return the active sections.
 * @param  {Object} packageData The full package data.
 * @param  {string} currentSectionType The current type fo section.
 * @return {array}  active sections.
 */
export const getSectionsType = (packageData, currentSectionType) => {
  return packageData.sections.filter(section => section.sectionType === currentSectionType);
};

/**
 * Gets the correct hash to use for this state.
 * @param  {Object} state The section state to generate the hash from.
 * @return {string}       The hash string.
 */
export const getStateHash = state => {
  const {
    currentSectionId,
    currentPageData: {
      currentPageId,
      currentStepData: {
        currentStepId,
      },
    },
  } = state;

  return `${currentSectionId}/${currentPageId}/${currentStepId}`;
};

/**
 * Returns true if the step is the very first in the flow.
 * @param  {Object}  currentProgress The current progress object.
 * @return {Boolean}                 True if this is the first step.
 */
export const isFirstStep = (currentProgress) => {
  const {
    currentSectionId,
    sectionOrder,
    currentPageData: {
      currentPageId,
      pagesOrder,
      currentStepData: {
        currentStepId,
        stepsOrder,
      },
    },
  } = currentProgress;

  if (!currentSectionId) {
    return true;
  }
  // Is first section?
  let isFirst = currentSectionId === sectionOrder[0];

  // Is first page?
  isFirst = (isFirst && (currentPageId === pagesOrder[0]));

  // Is first section?
  isFirst = (isFirst && (currentStepId === stepsOrder[0]));

  return isFirst;
};

/**
 * Returns a boolean of whether the progress bar should be rendered
 * @param   {boolean} shouldHideProgress    Boolean set in the package.file
 * @param   {object} sectionData            Object containing the section progress
 * @param   {object} currentPageData        Object containing the current page data
 * @returns {boolean}
 */
export const hideProgressBar = (shouldHideProgress, sectionData, currentPageData) => {
  let hiding = false;
  if (shouldHideProgress === true) {
    hiding = true;
  }
  const { currentPageId } = currentPageData;
  if (Object.keys(sectionData).length > 0) {
    const { pages } = sectionData;
    pages.forEach(page => {
      const { pageId, hideProgress } = page;
      if (hideProgress && currentPageId === pageId) {
        hiding = true;
      }
    });
  }
  return hiding;
};

/**
 * Returns a boolean of whether the current progress bar should be rendered
 * @param   {boolean} shouldHideCurrentProgress    Boolean set in the package.file
 * @returns {boolean}
 */
export const hideCurrentProgressBar = (shouldHideCurrentProgress) => {
  let hiding = false;
  if (shouldHideCurrentProgress === true) {
    hiding = true;
  }
  return hiding;
};

/**
 * Returns a boolean of whether the progress bar should be rendered
 * @param   {boolean} shouldHideBackButton  Boolean set in the package.file
 * @param   {object} sectionData            Object containing the section progress
 * @param   {object} currentPageData        Object containing the current page data
 * @returns {boolean}
 */
export const hideBackButtonLink = (shouldHideBackButton, sectionData, currentPageData) => {
  let hiding = false;
  if (shouldHideBackButton === true) {
    hiding = true;
  }
  const { currentPageId } = currentPageData;
  if (Object.keys(sectionData).length > 0) {
    const { pages } = sectionData;
    pages.forEach(page => {
      const { pageId, hideBackButton } = page;
      if (hideBackButton && currentPageId === pageId) {
        hiding = true;
      }
    });
  }
  return hiding;
};

/**
 * Making animation different page transition time
 * @param   {string} pageType     String of the different page types
 * @param   {string} loaderType   The loader variant
 * @returns {object}
 */
export const pageTransitionType = (pageType, loaderType = 'slideInTop') => {
  switch (pageType) {
    case 'Loader': {
      return {
        timeout: 300,
        classNames: loaderType,
      };
    }
    case 'Page':
    default:
      return {
        timeout: 300,
        classNames: 'slideInTop',
      };
  }
};

/**
 * Gets the current ids from the hash.
 * @param  {string} hash The hash without the #.
 * @return {Object}      Object with sectionId, pageId, stepId.
 */
export const getIdsFromHash = hash => {
  // Hash has the format section/page/step. Split up.
  const ids = hash.split('/');
  const sectionId = Number(ids[0]);
  const pageId = Number(ids[1]);
  const stepId = Number(ids[2]);

  return {
    sectionId,
    pageId,
    stepId,
  };
};

/**
 * Finds and returns the section, page, step defined by the hash.
 * @param  {string} hash        The hash path without the actual hash.
 * @param  {Object} packageData The full package data.
 * @return {Object}             An object with keys section, page and step.
 */
export const getSectionPageStepFromHash = (hash, packageData) => {
  // Hash has the format section/page/step. Split up.
  const { sectionId, pageId, stepId } = getIdsFromHash(hash);

  const section = getSectionById(sectionId, packageData);
  const page = getPageById(pageId, section);
  const step = getStepById(stepId, page);

  return {
    section,
    page,
    step,
  };
};

/**
 * Checks that the given hash is actually the first step.
 * @param  {Object}  sectionState The current state of section store.
 * @return {Boolean}              True if this is the first step.
 */
export const isHashFirstStep = (sectionState) => {
  // Fetch the ids from the hash and compare them to section-, page and step order.
  const { currentHash } = sectionState;
  const { sectionId, pageId, stepId } = getIdsFromHash(currentHash);

  const { sectionOrder } = sectionState;
  if (sectionOrder[0] !== sectionId) {
    return false;
  }

  const { currentPageData: { pagesOrder } } = sectionState;
  if (pagesOrder[0] !== pageId) {
    return false;
  }

  const { currentPageData: { currentStepData: { stepsOrder } } } = sectionState;

  return stepsOrder[0] === stepId;
};



// WEBPACK FOOTER //
// ./src/utilities/section/sectionUtils.js